import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Shield, Clock, MapPin, ArrowRight, AlertTriangle, CheckCircle, Star, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { mockData } from '../mock';
import HowItWorks from '../components/HowItWorks';
import FAQ from '../components/FAQ';

const Home = () => {
  const handleCall = () => {
    window.location.href = 'tel:0676583038';
  };

  const scrollToContact = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section - Immersive */}
      <section className="relative bg-gradient-to-br from-yellow-50 via-white to-gray-50 py-20 lg:py-32 overflow-hidden">
        {/* Background Pattern & Effects */}
        <div className="absolute inset-0">
          {/* Animated Background Elements */}
          <div className="absolute top-20 left-10 w-32 h-32 border-2 border-yellow-400 rounded-full opacity-20 animate-pulse-slow"></div>
          <div className="absolute top-40 right-20 w-24 h-24 border-2 border-black rounded-full opacity-10 animate-float"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 border-2 border-yellow-400 rounded-full opacity-15"></div>
          
          {/* Gradient Overlays */}
          <div className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-yellow-100 to-transparent opacity-30"></div>
          <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-to-tl from-gray-100 to-transparent opacity-20"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Hero Content */}
            <div className="space-y-8">
              {/* Alert Badge */}
              <div className="flex items-center space-x-2 text-red-600 font-medium bg-red-50 px-4 py-2 rounded-full w-fit border border-red-200">
                <AlertTriangle className="h-4 w-4" />
                <span>Intervention d'urgence 24h/24</span>
              </div>

              {/* Main Headline */}
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  <span className="block">Frelons ? Guêpes ?</span>
                  <span className="block text-yellow-600">Intervention express</span>
                  <span className="block">en - de 48h</span>
                </h1>
                <p className="text-xl md:text-2xl text-gray-600 leading-relaxed">
                  <strong className="text-black">Expert anti-nuisibles en Gironde</strong><br />
                  Agréé • Certifié • Disponible 7j/7<br />
                  <span className="text-2xl font-bold text-black">☎️ 06 76 58 30 38</span>
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleCall}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 text-xl rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg"
                >
                  <Phone className="h-6 w-6" />
                  <span>📞 06 76 58 30 38</span>
                </Button>
                <Button
                  onClick={scrollToContact}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-8 py-4 text-xl rounded-lg transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg"
                >
                  <span>🚨 Demander une intervention</span>
                  <ArrowRight className="h-6 w-6" />
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-4">
                <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm p-3 rounded-lg border border-gray-200">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">Agréé & certifié</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm p-3 rounded-lg border border-gray-200">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium">7j/7 - 24h/24</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm p-3 rounded-lg border border-gray-200">
                  <MapPin className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-medium">Toute la Gironde</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm p-3 rounded-lg border border-gray-200">
                  <Star className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm font-medium">Satisfaction garantie</span>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="relative z-10 transform hover:scale-105 transition-transform duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_frelon33-experts/artifacts/xehwbzjb_pulv%C3%A9riser.png"
                  alt="Intervention professionnelle Frelon33" 
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                
                {/* Floating Badge */}
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg border-l-4 border-yellow-400 animate-float">
                  <div className="flex items-center space-x-4">
                    <div className="bg-yellow-100 p-3 rounded-full">
                      <Shield className="h-8 w-8 text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-lg">Intervention sécurisée</p>
                      <p className="text-gray-600">Équipement professionnel</p>
                    </div>
                  </div>
                </div>

                {/* Certification Badge */}
                <div className="absolute -top-4 -right-4 bg-green-500 text-white p-3 rounded-full shadow-lg">
                  <Award className="h-6 w-6" />
                </div>
              </div>

              {/* Background Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl blur-3xl opacity-20 -z-10 transform scale-110"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages Section - Enhanced */}
      <section className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi choisir <span className="text-yellow-600">Frelon33</span> ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              L'expertise, la rapidité et la sécurité au service de votre tranquillité
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.guarantees.map((guarantee, index) => {
              const Icon = {
                Clock,
                Shield,
                MapPin,
                Phone
              }[guarantee.icon];

              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-4 border-t-4 border-yellow-400 bg-gradient-to-br from-white to-gray-50">
                  <CardContent className="p-8 text-center">
                    <div className="bg-yellow-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-yellow-200 transition-colors group-hover:scale-110 transform duration-300">
                      <Icon className="h-10 w-10 text-yellow-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {guarantee.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {guarantee.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Component */}
      <HowItWorks />

      {/* Emergency Alert Section - Enhanced */}
      <section className="py-20 bg-gradient-to-r from-red-600 via-red-700 to-red-800 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-20 w-40 h-40 border-2 border-white rounded-full animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-10 w-32 h-32 border-2 border-yellow-400 rounded-full animate-float"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex items-center space-x-3 mb-6">
                <AlertTriangle className="h-10 w-10 text-yellow-400 animate-pulse" />
                <h2 className="text-3xl md:text-4xl font-bold">
                  🚨 Urgence frelon asiatique
                </h2>
              </div>
              
              <div className="space-y-6">
                <p className="text-xl leading-relaxed">
                  Le frelon asiatique représente un <strong>danger immédiat</strong> pour la biodiversité 
                  et peut être extrêmement agressif en cas d'approche du nid.
                </p>
                
                <div className="bg-red-800/50 backdrop-blur-sm border border-red-500 rounded-lg p-6">
                  <h3 className="text-lg font-bold mb-3 text-yellow-400">⚠️ Signes d'urgence :</h3>
                  <ul className="space-y-2 text-sm">
                    <li>• Nid visible et actif</li>
                    <li>• Frelons près des zones de passage</li>
                    <li>• Comportement agressif observé</li>
                    <li>• Proximité d'enfants ou animaux</li>
                  </ul>
                </div>

                <p className="text-lg font-bold text-yellow-400">
                  ⏰ N'attendez pas, contactez-nous IMMÉDIATEMENT !
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleCall}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-8 py-4 text-xl rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  <Phone className="h-6 w-6 mr-2" />
                  Urgence : 06 76 58 30 38
                </Button>
                <Link to="/frelon-asiatique">
                  <Button
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-red-600 font-semibold px-8 py-4 text-xl rounded-lg transition-all duration-200 w-full sm:w-auto"
                  >
                    En savoir plus
                    <ArrowRight className="h-6 w-6 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative z-10 transform hover:scale-105 transition-transform duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_frelon33-experts/artifacts/ap01dldn_grosfrelons.jpg"
                  alt="Frelon asiatique - Danger immédiat" 
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                
                {/* Warning Overlay */}
                <div className="absolute top-4 right-4 bg-red-600 text-white p-3 rounded-full shadow-lg animate-pulse">
                  <AlertTriangle className="h-6 w-6" />
                </div>
              </div>
              
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl blur-3xl opacity-30 -z-10 transform scale-110"></div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Component */}
      <FAQ />

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-400 to-yellow-500 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-32 h-32 border-4 border-black rounded-full animate-pulse-slow"></div>
          <div className="absolute bottom-10 right-10 w-24 h-24 border-4 border-black rounded-full animate-float"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="space-y-8">
            <h2 className="text-3xl md:text-4xl font-bold text-black">
              🐝 Prêt à retrouver votre tranquillité ?
            </h2>
            <p className="text-xl text-black leading-relaxed max-w-3xl mx-auto opacity-90">
              Notre équipe d'experts certifiés intervient <strong>7j/7, 24h/24</strong> sur toute la Gironde. 
              Devis gratuit, intervention rapide, satisfaction garantie.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button
                onClick={handleCall}
                className="bg-black hover:bg-gray-800 text-white font-bold px-10 py-5 text-xl rounded-lg transition-all duration-200 transform hover:scale-105 shadow-xl"
              >
                <Phone className="h-6 w-6 mr-2" />
                📞 06 76 58 30 38
              </Button>
              <Button
                onClick={scrollToContact}
                variant="outline"
                className="border-4 border-black text-black hover:bg-black hover:text-white font-bold px-10 py-5 text-xl rounded-lg transition-all duration-200 transform hover:scale-105"
              >
                <span>📝 Devis gratuit en ligne</span>
                <ArrowRight className="h-6 w-6 ml-2" />
              </Button>
            </div>

            {/* Trust Elements */}
            <div className="flex flex-wrap justify-center gap-6 pt-8">
              <div className="flex items-center space-x-2 bg-black/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Shield className="h-5 w-5 text-black" />
                <span className="font-semibold text-black">Agréé Ministère Agriculture</span>
              </div>
              <div className="flex items-center space-x-2 bg-black/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Clock className="h-5 w-5 text-black" />
                <span className="font-semibold text-black">Intervention sous 48h</span>
              </div>
              <div className="flex items-center space-x-2 bg-black/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Star className="h-5 w-5 text-black" />
                <span className="font-semibold text-black">Satisfaction 100%</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;